import { useCallback } from 'react'
import { VerifyBulkInsertTasksResponse } from '@crew/apis/task/models/verifyBulkInsertTasks/response'
import { BulkInsertTasksRequest } from '@crew/apis/task/models/bulkInsertTasks/request'
import {
  useBulkInsertTasksMutation,
  useVerifyBulkInsertTasksMutation,
} from '@crew/apis/task/taskApis'
import { EntityType } from '@crew/enums/domain'
import { JsonDateFormat } from '@crew/enums/system'
import { Task } from '@crew/models/domain'
import dayjs from '@crew/modules'
import { convertScheduledTimeToMinutes } from '@crew/utils'
import { NotifyEventType } from 'enums/app'
import { useAppDispatch } from 'states/hooks'
import {
  ObjectEventMessage,
  notifyTaskEvent,
} from 'features/app/states/appSlice'

export const useTaskImportCsvDialog = (projectId: string) => {
  const dispatch = useAppDispatch()

  const [verifyBulkInsertTasksMutation] = useVerifyBulkInsertTasksMutation()
  const [bulkInsertTasksMutation, { isLoading: isLoadingBulkInsertTasks }] =
    useBulkInsertTasksMutation()

  // タスク一括登録の検証
  const verifyBulkInsertTasks = useCallback(
    async (file: File) => {
      const result = await verifyBulkInsertTasksMutation({
        entityType: EntityType.Project,
        entityRecordId: projectId,
        file: file,
      }).unwrap()

      return result
    },
    [projectId, verifyBulkInsertTasksMutation]
  )

  // タスク一括登録処理
  const bulkInsertTasks = useCallback(
    async (verifyResponse: VerifyBulkInsertTasksResponse) => {
      const insertTasks = verifyResponse.data.map((row) => {
        // 予定時間が設定されている場合はHH:mmを分に変換する
        if (row.estimatedWorkTimes) {
          const minutes = convertScheduledTimeToMinutes(
            row.estimatedWorkTimes.value
          )
          // 分に変換できた場合は変換結果をセットする
          if (minutes !== undefined) {
            row = {
              ...row,
              estimatedWorkTimes: {
                ...row.estimatedWorkTimes,
                value: String(minutes),
              },
            }
          }
        }

        // 開始日がある場合はYYYY-MM-DD形式に変換する
        if (row.startDate) {
          const startDate = dayjs(row.startDate.value).format(
            JsonDateFormat.YYYYMMDD
          )
          row = {
            ...row,
            startDate: {
              ...row.startDate,
              value: startDate,
            },
          }
        }

        // 期限がある場合はYYYY-MM-DD形式に変換する
        if (row.dueDate) {
          const dueDate = dayjs(row.dueDate.value).format(
            JsonDateFormat.YYYYMMDD
          )
          row = {
            ...row,
            dueDate: {
              ...row.dueDate,
              value: dueDate,
            },
          }
        }

        return row
      })

      const params: BulkInsertTasksRequest = {
        entityType: EntityType.Project,
        entityRecordId: projectId,
        tasks: insertTasks,
      }

      await bulkInsertTasksMutation(params).unwrap()

      // タスクが登録されたことを通知する
      const objectEventMessage: ObjectEventMessage<Task> = {
        eventType: NotifyEventType.Inserted,
        id: '',
        object: undefined,
      }

      dispatch(notifyTaskEvent(objectEventMessage))
    },
    [bulkInsertTasksMutation, dispatch, projectId]
  )

  return { verifyBulkInsertTasks, bulkInsertTasks, isLoadingBulkInsertTasks }
}
